import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const Hero = ({ slides = [], loading }) => {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);

  const handleNext = useCallback(() => {
    setCurrent((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  }, [slides.length]);

  const handlePrev = useCallback(() => {
    setCurrent((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  }, [slides.length]);

  const handleAction = (link) => {
    if (!link) {
      navigate('/productos');
      return;
    }

    if (link.startsWith('#')) {
      const targetId = link.substring(1);
      const element = document.getElementById(targetId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      } else {
        navigate('/'); 
      }
    } 
    else if (link.includes('?')) {
      const [path, query] = link.split('?');
      navigate({ pathname: path, search: query });
    }

    else {
      navigate(link);
    }
  };

  useEffect(() => {
    if (slides.length <= 1) return;
    const timer = setInterval(handleNext, 8000);
    return () => clearInterval(timer);
  }, [handleNext, slides.length]);

  const onDragEnd = (e, { offset }) => {
    const swipeThreshold = 50;
    if (offset.x > swipeThreshold) handlePrev();
    else if (offset.x < -swipeThreshold) handleNext();
  };

  const active = slides[current];

  if (loading || !slides || slides.length === 0) return (
    <section className="h-[80vh] bg-white flex items-center justify-center">
      <div className="w-10 h-10 border-4 border-[#a6192e] border-t-transparent rounded-full animate-spin"></div>
    </section>
  );

  return (
    <section id="inicio" className="relative h-[90vh] w-full bg-white overflow-hidden flex items-center justify-center touch-none group">
      
      <AnimatePresence mode="wait">
        <motion.div
          key={current}
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.2}
          onDragEnd={onDragEnd}
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.8 }}
          className="absolute inset-0 z-0 cursor-grab active:cursor-grabbing"
        >
          {active.background && (
            <div className="w-full h-full relative pointer-events-none">
              <img src={active.background} className="w-full h-full object-cover" alt="" />
              <div className="absolute inset-0 bg-black/30" />
              <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/50 via-transparent to-transparent z-10" />
              <div className="absolute bottom-0 left-0 right-0 h-64 bg-gradient-to-t from-white via-white/40 to-transparent z-20" />
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      <div className="hidden md:block">
        <button 
          onClick={handlePrev}
          className="absolute left-8 top-1/2 -translate-y-1/2 z-40 w-12 h-12 flex items-center justify-center rounded-full bg-white/5 border border-white/10 backdrop-blur-md text-white opacity-0 group-hover:opacity-100 transition-all hover:bg-[#a6192e] shadow-2xl"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <button 
          onClick={handleNext}
          className="absolute right-8 top-1/2 -translate-y-1/2 z-40 w-12 h-12 flex items-center justify-center rounded-full bg-white/5 border border-white/10 backdrop-blur-md text-white opacity-0 group-hover:opacity-100 transition-all hover:bg-[#a6192e] shadow-2xl"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      <div className="relative z-30 text-center px-6 max-w-5xl pointer-events-none">
        <AnimatePresence mode="wait">
          <motion.div 
            key={current + "c"} 
            initial={{ y: 30, opacity: 0 }} 
            animate={{ y: 0, opacity: 1 }} 
            exit={{ y: -30, opacity: 0 }}
          >
            <h1 className="text-5xl md:text-[8.5rem] font-black italic tracking-tighter uppercase leading-[0.8] text-white drop-shadow-[0_15px_35px_rgba(0,0,0,0.5)] mb-6">
              {active.title}
            </h1>
            <p className="text-white/90 text-lg md:text-2xl font-medium max-w-2xl mx-auto italic mb-10 drop-shadow-lg">
              {active.subtitle}
            </p>
            
            <button 
              onClick={() => handleAction(active.link)}
              className="px-16 py-5 bg-[#a6192e] text-white rounded-full font-black text-[11px] tracking-[0.5em] uppercase pointer-events-auto shadow-2xl transition-all hover:scale-105 active:scale-95 border border-transparent hover:border-white/20"
            >
              {active.buttonText || 'DESCUBRIR'}
            </button>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="absolute bottom-10 flex gap-4 z-50">
        {slides.map((_, i) => (
          <button 
            key={i} 
            onClick={() => setCurrent(i)}
            className={`h-2 rounded-full transition-all duration-500 shadow-xl ${
              current === i 
                ? 'w-16 bg-[#a6192e]' 
                : 'w-6 bg-slate-900/40 hover:bg-slate-900/60'
            }`}
          />
        ))}
      </div>
    </section>
  );
};

export default Hero;